/**
 * Turning a runsheet into a chain-of-title chart.
 *
 * A runsheet row already says everything an arrow needs — who conveyed, who
 * received, by what instrument, on what date. So the chart is built from the
 * runsheet rather than drawn from scratch: an agent who adds a deed gets the
 * box and the arrow without touching the canvas, which is the difference
 * between this and paying for a blank drawing tool.
 *
 * Everything here is pure. The canvas is hard to test and the matching and
 * layout are where this would quietly go wrong — two boxes for one person, an
 * arrow pointing the wrong way down the chain — so all of that lives here.
 */
import { classifyInstrument, splitPartyNames } from './chartOwnership.js';

/**
 * The key two spellings of one party have to share.
 *
 * Deeds are inconsistent about case, punctuation and entity suffixes, and a
 * chain that shows "Quail Ridge Minerals LLC" and "QUAIL RIDGE MINERALS, L.L.C."
 * as two owners is worse than useless — it reads as a conveyance that never
 * happened. Suffixes are stripped rather than normalised because they are the
 * part deeds vary most and carry least.
 */
export function partyKey(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[.,'"]/g, '')
    .replace(/\b(l\s?l\s?c|l\s?p|inc|incorporated|corp|corporation|co|company|ltd|limited|trust|estate of|the)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** First occurrence wins, so the order the deed names people in is kept. */
function unique(list) {
  return [...new Set(list)];
}

/** What kind of party a name reads as, for the box's shape and icon. */
export function partyKind(name) {
  const text = String(name || '').toLowerCase();
  if (/\bestate of\b|\bdeceased\b|\bdec'?d\b/.test(text)) return 'estate';
  if (/\btrust\b|\btrustee\b|\brevocable\b/.test(text)) return 'trust';
  // "Co" with no full stop is how half the county records write it — Wheeler
  // Ranch Co, Permian Land Co — and it is only a company when it ends the
  // name, so this does not catch a middle initial or "c/o".
  if (/\bllc\b|\bl\.l\.c\b|\binc\b|\bcorp\b|\bcompany\b|\bltd\b|\blp\b|\bpartnership\b/.test(text)
    || /\bco\.?\s*$/.test(text.trim())) {
    return 'company';
  }
  if (!text.trim()) return 'unknown';
  return 'person';
}

/** "Warranty Deed · 812/44" — the heading on the deed box. */
export function linkLabel(row) {
  return [row.instrument, row.vp].filter(Boolean).join(' · ') || 'Conveyance';
}

/**
 * Every party and conveyance a set of runsheet rows implies.
 *
 * Rows left off the runsheet are left out of the chart too: an excluded row is
 * one somebody decided does not belong in the chain, and it should not come
 * back as an arrow.
 */
export function chartFromRunsheet(rows = [], { land = '' } = {}) {
  const parties = new Map();
  const links = [];

  const remember = (name, tractId) => {
    const key = partyKey(name);
    if (!key) return null;
    if (!parties.has(key)) {
      parties.set(key, {
        match_key: key,
        label: String(name).trim(),
        kind: partyKind(name),
        tract_id: tractId || null,
        origin: 'auto',
      });
    }
    return key;
  };

  const ordered = [...rows]
    .filter((r) => !r.excluded)
    .sort((a, b) => String(a.effective_date || '9999').localeCompare(String(b.effective_date || '9999')));

  for (const row of ordered) {
    const grantors = splitPartyNames(row.grantor);
    const grantees = splitPartyNames(row.grantee);
    // A row naming only one side still tells us that party exists, which is
    // worth a box even when there is no arrow to draw yet.
    grantors.forEach((g) => remember(g, row.tract_id));
    grantees.forEach((g) => remember(g, row.tract_id));

    // One instrument is one deed box, however many people signed it. Drawing
    // a box per pair — four for a couple conveying to two children — put the
    // same deed on the chart four times, each a quarter of what happened, and
    // there was no single thing to click to read the instrument.
    const fromKeys = unique(grantors.map(partyKey).filter(Boolean));
    const toKeys = unique(grantees.map(partyKey).filter(Boolean));
    // Somebody named on both ends did not convey to themselves, so they are
    // taken off whichever side still leaves an arrow to draw. "Sam and Ruth to
    // Sam" is Ruth conveying her half to Sam; "Sam to Sam and Ruth" is Sam
    // conveying half to Ruth; and "Sam to Sam" is a correction that moved
    // nothing and gets no arrow at all.
    const overlap = fromKeys.filter((k) => toKeys.includes(k));
    const givingOnly = fromKeys.filter((k) => !overlap.includes(k));
    const takingOnly = toKeys.filter((k) => !overlap.includes(k));
    if (!fromKeys.length || !toKeys.length) continue;
    if (!givingOnly.length && !takingOnly.length) continue;
    // Whoever is left on each side after that. Falling back to the full list
    // keeps the party who stayed put on the end they belong to rather than
    // dropping the conveyance for want of somebody to hang it off.
    const giving = givingOnly.length ? givingOnly : fromKeys;
    const taking = takingOnly.length ? takingOnly : toKeys;

    links.push({
      fromKey: giving[0],
      toKey: taking[0],
      // Everybody else who signed. They become boxes of their own, joined to
      // this one deed, so each can be followed into the instrument where they
      // sold separately later.
      extraFromKeys: giving.slice(1),
      extraToKeys: taking.slice(1),
      runsheet_row_id: row.id,
      document_id: row.document_id || null,
      label: linkLabel(row),
      kind: classifyInstrument(row.instrument),
      // The four things an examiner reads off a deed. They come straight
      // from the runsheet row, which already carries them — the chart is
      // not asking anybody to type them a second time.
      effective_date: row.effective_date || null,
      // A runsheet row has no land column of its own — the tract it is
      // filed against is the land — so the caller passes that in and a row
      // that names something narrower overrides it.
      land: String(row.land || land || '').trim(),
      interest_conveyed: String(row.interest_granted || '').trim(),
      interest_reserved: String(row.interest_reserved || '').trim(),
      origin: 'auto',
    });
  }

  return { parties: [...parties.values()], links };
}

/**
 * The kinds of interest a party can be left holding.
 *
 * A closed list because these four are what gets reported on. A box reading
 * "min" or "royalty?" is a note to oneself, not an answer.
 */
export const INTEREST_TYPES = ['NPRI', 'MI', 'WI', 'ORRI', 'PP'];

/**
 * A generation is a row of siblings and a chain is a column of dates, so the
 * boxes spread sideways and stack downwards. The tier is deep enough to leave
 * room for a deed box in the gap, because that is what now sits there.
 */
const TIER_HEIGHT = 300;
const SIBLING_WIDTH = 250;
// The empty run between one chain and the next. Wide enough that the gap
// reads as a break rather than as another tier — a deed box sits in the gap
// between tiers, so anything tighter looks like it belongs to both chains.
const CHAIN_GAP = 260;

/**
 * Where each box goes, before anyone drags it.
 *
 * Laid out by depth down the chain rather than by date: the point of the
 * picture is that title flows from one party to the next, so a party sits one
 * tier below whoever conveyed to them. Dates order the boxes within a tier,
 * which keeps a busy generation readable.
 *
 * Top to bottom and nothing else. A chain of title is a family tree far more
 * often than it is a timeline, and one direction means one set of handles, one
 * layout to reason about, and no chart that reads differently depending on who
 * opened it.
 *
 * Cycles are possible in real title — land comes back to a family — so depth
 * is computed with a seen-set rather than by recursion that would not
 * terminate.
 */
/** Everybody a link touches, principals and the rest alike. */
function endsOf(link) {
  return {
    from: [link.fromKey, ...(link.extraFromKeys || [])].filter(Boolean),
    to: [link.toKey, ...(link.extraToKeys || [])].filter(Boolean),
  };
}

/**
 * The separate chains in a runsheet.
 *
 * A runsheet is very often not one chain. It is the deeds found for a name,
 * and they fall into several unconnected chains — one for each tract, or one
 * per branch of a family that never conveyed to the other. Drawn as a single
 * graph they interleave: every party lands in one global tier, so unrelated
 * chains share a row, arrows reach across the whole page to find their
 * partner, and forty boxes read as one impenetrable thing.
 *
 * Grouping by connected component is what makes them separate pictures. The
 * links are followed in both directions because a chain is a chain whichever
 * end you start from.
 */
export function chainsOf(parties, links) {
  const near = new Map(parties.map((p) => [p.match_key, new Set()]));
  const join = (a, b) => {
    if (!near.has(a) || !near.has(b) || a === b) return;
    near.get(a).add(b);
    near.get(b).add(a);
  };
  for (const link of links) {
    const { from, to } = endsOf(link);
    // Everyone who signed one deed belongs to one chain, grantors together,
    // grantees together, and across.
    for (const a of [...from, ...to]) for (const b of [...from, ...to]) join(a, b);
  }

  const seen = new Set();
  const chains = [];
  for (const party of parties) {
    if (seen.has(party.match_key)) continue;
    const group = [];
    const queue = [party.match_key];
    seen.add(party.match_key);
    while (queue.length) {
      const key = queue.shift();
      group.push(key);
      for (const next of near.get(key) || []) {
        if (seen.has(next)) continue;
        seen.add(next);
        queue.push(next);
      }
    }
    chains.push(new Set(group));
  }

  // The longest chain first — it is the one the assignment is usually about —
  // then the rest in the order the runsheet introduced them, so two builds of
  // the same rows produce the same picture.
  return chains
    .map((keys, index) => ({ keys, index }))
    .sort((a, b) => b.keys.size - a.keys.size || a.index - b.index)
    .map((c) => c.keys);
}

/**
 * Where each box goes, before anyone drags it.
 *
 * Laid out by depth down the chain rather than by date: the point of the
 * picture is that title flows from one party to the next, so a party sits one
 * tier below whoever conveyed to them. Dates order the boxes within a tier,
 * which keeps a busy generation readable.
 *
 * Top to bottom and nothing else. A chain of title is a family tree far more
 * often than it is a timeline, and one direction means one set of handles, one
 * layout to reason about, and no chart that reads differently depending on who
 * opened it.
 *
 * Each chain gets its own band down the page rather than sharing tiers with
 * the others. Two unrelated chains on one row is what made a forty-box build
 * unreadable: they interleave, and every arrow has to cross the page to find
 * the box it belongs to.
 *
 * Cycles are possible in real title — land comes back to a family — so depth
 * is computed with a seen-set rather than by recursion that would not
 * terminate.
 */
export function layoutParties(parties, links) {
  const incoming = new Map(parties.map((p) => [p.match_key, []]));
  for (const link of links) {
    // Everybody who signed, not just the pair the row itself names: a deed's
    // second grantee belongs on the same tier as its first, and a chain drawn
    // from the principals alone leaves the rest stacked at the top as though
    // they had held the land from the start.
    const { from, to } = endsOf(link);
    for (const key of to) {
      if (incoming.has(key)) incoming.get(key).push(...from);
    }
  }

  const depth = new Map();
  const depthOf = (key, seen = new Set()) => {
    if (depth.has(key)) return depth.get(key);
    if (seen.has(key)) return 0;
    seen.add(key);
    const parents = incoming.get(key) || [];
    const value = parents.length
      ? Math.max(...parents.map((p) => depthOf(p, seen) + 1))
      : 0;
    depth.set(key, value);
    return value;
  };

  const placed = [];
  let bandTop = 0;

  for (const chain of chainsOf(parties, links)) {
    const byTier = new Map();
    for (const party of parties) {
      if (!chain.has(party.match_key)) continue;
      const tier = depthOf(party.match_key);
      if (!byTier.has(tier)) byTier.set(tier, []);
      byTier.get(tier).push(party);
    }

    const tiers = [...byTier.entries()].sort((a, b) => a[0] - b[0]);
    const columnOf = new Map();
    for (const [tier, group] of tiers) {
      // Sat under whoever conveyed to them, by the average column of their
      // grantors. Without this, siblings keep the order the runsheet happened
      // to list them in and their arrows cross the whole tier to reach a
      // parent at the other end.
      const ordered = group
        .map((party, i) => {
          const parents = (incoming.get(party.match_key) || [])
            .map((key) => columnOf.get(key))
            .filter((c) => c !== undefined);
          const anchor = parents.length
            ? parents.reduce((sum, c) => sum + c, 0) / parents.length
            : i;
          return { party, anchor, i };
        })
        .sort((a, b) => a.anchor - b.anchor || a.i - b.i);

      ordered.forEach(({ party }, column) => {
        columnOf.set(party.match_key, column);
        placed.push({ ...party, x: column * SIBLING_WIDTH, y: bandTop + tier * TIER_HEIGHT });
      });
    }

    const deepest = tiers.length ? tiers[tiers.length - 1][0] : 0;
    bandTop += (deepest + 1) * TIER_HEIGHT + CHAIN_GAP;
  }

  return placed;
}

